import { useMemo } from 'react'
import PropTypes from 'prop-types'
import Label from 'components/Label'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import { borderRadius, borderWidth } from 'theme/globals'
import { isNil, find, map, isNumber, isString, flatten } from 'lodash'

export const renderMenuItems = (items, id, valueKey) =>
  map(items, (item, value) => (
    <MenuItem
      key={`${id}_${value}`}
      value={isNumber(item) || (isString(item) && !valueKey) ? item : isNumber(item.value) ? item.value : item.value || value}
    >
      {item?.name || item?.value?.item || item?.value || item}
    </MenuItem>
  ))

const WrappedSelect = props => {
  const {
    placeholder,
    color,
    colorActive,
    bgVariant,
    bgVariantActive,
    borderColor,
    borderWidth,
    label,
    children,
    labelProps,
    selectSX,
    ...otherProps
  } = props
  const activePlaceholder = useMemo(() => {
    const temp = find(flatten(children), o => o?.props?.value === otherProps.value)
    return isNil(temp?.props?.children) ? placeholder : temp.props.children
  }, [children, otherProps.value, placeholder])

  return (
    <Stack
      gap={1}
      width="100%"
    >
      {label && (
        <Label
          {...labelProps}
        >
          {label}
        </Label>
      )}
      <Select
        sx={theme => ({
          color,
          backgroundColor: `background.${bgVariant}.main`,
          borderColor,
          borderWidth: theme.globals.borderWidth[borderWidth],
          '&:hover, &:active, &.Mui-focused': {
            color: colorActive,
            backgroundColor: `background.${bgVariantActive}.main`,
          },
          '@media (hover: none) and (pointer: coarse)': {
            '&:hover, &:active, &.Mui-focused': {
              backgroundColor: 'initial', // Or your desired color
            },
          },
          ...selectSX,
        })}
        displayEmpty
        placeholder={activePlaceholder}
        renderValue={selected => activePlaceholder}
        {...otherProps}
      >
        {children}
      </Select>
    </Stack>
  )
}

WrappedSelect.propTypes = {
  id: PropTypes.string,
  label: PropTypes.any,
  labelColor: PropTypes.any,
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.oneOf(['default', 'shadow', 'line', 'filled']),
  maxWidth: PropTypes.string,
  color: PropTypes.any,
  colorActive: PropTypes.any,
  borderColor: PropTypes.any,
  borderActive: PropTypes.any,
  borderWidth: PropTypes.oneOf(Object.keys(borderWidth)),
  bgVariant: PropTypes.any,
  bgVariantActive: PropTypes.any,
  placeholder: PropTypes.any,
  radius: PropTypes.oneOf(Object.keys(borderRadius)),
  autocomplete: PropTypes.string,
  error: PropTypes.bool,
  required: PropTypes.bool,
  noPad: PropTypes.bool,
  noRotateIcon: PropTypes.bool,
  squareLeft: PropTypes.bool,
  squareRight: PropTypes.bool,
  spacing: PropTypes.oneOf(['default', 'tight']),
  size: PropTypes.oneOf(['default', 'short']),
  flip: PropTypes.bool,
  isActive: PropTypes.bool,
  disabled: PropTypes.bool,
  delayOpen: PropTypes.number,
  lock: PropTypes.bool,
  optional: PropTypes.bool,
  optionalLabel: PropTypes.any,
  labelProps: PropTypes.object,
  selectSX: PropTypes.object,
}

WrappedSelect.defaultProps = {
  color: 'text.neutralCool_700',
  colorActive: 'text.neutralCool_700',
  borderWidth: 'thick',
  bgVariant: 'paper',
  bgVariantActive: 'neutral_300',
  placeholder: 'Select One',
  labelProps: {},
  selectSX: {},
}

export default WrappedSelect
